/**
 * Dashboard plan-usage API (ADR-0263 S3) — merchant read of the
 * account's v2 plan assignment and current-month usage per site.
 *
 * Read-only. `active: false` means every site is `plan_source: 'none'`
 * (the default while the v2 catalog is draft); the UI hides itself.
 */

import type { HttpClient } from './http-client.js';

export type PlanUsageDimension =
  | 'page_loads_monthly'
  | 'orders_monthly'
  | 'assistant_requests_monthly';

export type PlanUsageState = 'ok' | 'near' | 'over' | 'exceeded' | 'unlimited';

export interface PlanUsageMeter {
  readonly dimension: PlanUsageDimension;
  readonly scope: 'site' | 'account';
  readonly usage: number;
  readonly limit: number | null;
  readonly base_limit: number | null;
  readonly addon_headroom: number;
  /** ADR-0263 §7.1 — `limit × (1 + overage_pct / 100)`; null = unlimited. */
  readonly allowed: number | null;
  /** ADR-0263 §7.1 — the plan's overage allowance, 0–100. */
  readonly overage_pct: number;
  readonly percent: number | null;
  readonly state: PlanUsageState;
  /** ADR-0263 §7.2 (S8) — ISO-8601 UTC of the first pause tick when
   *  this meter is past allowed for the current month; null otherwise.
   *  The banner uses it to switch to the pause copy. */
  readonly paused_at: string | null;
}

export interface PlanUsagePlanRef {
  readonly plan_id: string;
  readonly slug: string;
  readonly name: string;
}

export interface PlanUsagePending {
  readonly plan: PlanUsagePlanRef;
  readonly effective_at: string;
}

export interface PlanUsageSite {
  readonly site_id: string;
  readonly plan: PlanUsagePlanRef | null;
  readonly plan_source: 'assigned' | 'default' | 'agency' | 'none';
  readonly pending: PlanUsagePending | null;
  readonly features: readonly string[];
  readonly page_loads: PlanUsageMeter;
  readonly orders: PlanUsageMeter;
}

export interface PlanUsageAgencyInfo {
  readonly agency_account_id: string;
  readonly agency_name: string;
}

export interface PlanUsageAccountSummary {
  readonly plan: PlanUsagePlanRef | null;
  readonly plan_source: 'assigned' | 'default' | 'agency' | 'none';
  readonly features: readonly string[];
  /** ADR §2.3 — the ONE account-level assistant_requests reading.
   *  Null when the account is not on v2 yet. */
  readonly assistant_requests: PlanUsageMeter | null;
  /** ADR §2.6 — agency context for an agency-paid client account.
   *  Null when the client pays for itself. The dashboard section
   *  labels sites with `plan_source: 'agency'` using this name. */
  readonly agency: PlanUsageAgencyInfo | null;
}

export interface PlanUsageResponse {
  readonly account_id: string;
  readonly active: boolean;
  /** ADR-0263 §7.2 (S8) — master switch mirror. When false the
   *  server has the pause ledger live but is NOT acting on it; the
   *  dashboard banner shows "Nothing is blocked." in that case. */
  readonly enforcement_live: boolean;
  readonly period_start: string;
  readonly period_end: string;
  readonly account: PlanUsageAccountSummary;
  readonly sites: readonly PlanUsageSite[];
}

export type PlanUsageResult =
  | { readonly kind: 'ok'; readonly data: PlanUsageResponse }
  | { readonly kind: 'error' };

export async function getPlanUsage(http: HttpClient): Promise<PlanUsageResult> {
  try {
    const res = await http.request<PlanUsageResponse>({
      method: 'GET',
      path: '/v1/plan-usage',
    });
    if (res === undefined) return { kind: 'error' };
    return { kind: 'ok', data: res };
  } catch {
    return { kind: 'error' };
  }
}
